import React from 'react';
import { Badge, Button, Col, Modal, ModalBody, ModalHeader, Row } from 'reactstrap';

import { AppTile, TypePill, sourceLabel, typeLabel } from './rowParts';
import { isVideo, PLACEMENTS, posterSrc } from './advertModel';

const price = value => {
  if (String(value).trim() === '') return 'Free';
  const amount = Number(value);
  if (Number.isNaN(amount) || amount === 0) return 'Free';
  return `$${amount.toFixed(2)}`;
};

const placementName = value => (PLACEMENTS.find(p => p.value === value) || {}).name || value || '—';

/**
 * How the advert will look in a store's App Store (product feedback, UN-3248): the card as the store
 * admin sees it on the store tab, and the detail dialog they open from it — both drawn from the form
 * state, so this is what Save + Publish would put in front of every store. Layout mirrors cb-store's
 * consumer card and detail dialog; the Install button is decorative here.
 */
const AdvertStorePreview = ({ isOpen, form, linkedApp, advert, onClose }) => {
  const installCount = (linkedApp && linkedApp.installCount) || (advert && advert.installCount) || 0;
  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>Preview in the App Store</ModalHeader>
      <ModalBody>
        <Row>
          <Col md="5">
            <div className="text-muted text-uppercase mb-2" style={{ fontSize: 11, letterSpacing: '.05em' }}>
              On the store tab
            </div>
            <div className="border rounded p-3" style={{ maxWidth: 300 }}>
              <div className="d-flex justify-content-center mb-2">
                <AppTile name={form.name} imageUrl={form.imageUrl} size={120} />
              </div>
              <div className="fw-semibold text-truncate">{form.name || 'Untitled app'}</div>
              <div className="d-flex justify-content-between text-muted" style={{ fontSize: 13 }}>
                <span>{form.author || 'commercebuild'}</span>
                <span>
                  <i className="icon-cloud-download me-1" />
                  {installCount}
                </span>
              </div>
              <div className="mt-2" style={{ fontSize: 13, height: 40, overflow: 'hidden' }}>
                {form.description || <span className="text-muted">No description yet.</span>}
              </div>
              <div className="d-flex justify-content-between align-items-center mt-2">
                <span className="fw-semibold">{price(form.monthlyPrice)}</span>
                <Button color="dark" size="sm" disabled>
                  Install
                </Button>
              </div>
              <div className="mt-2">
                {form.featured ? <Badge color="warning" className="me-1">Featured</Badge> : null}
                {form.mostPopular ? <Badge color="info" className="me-1">Most popular</Badge> : null}
                {form.preinstalled ? <Badge color="secondary">Pre-installed</Badge> : null}
              </div>
            </div>
          </Col>
          <Col md="7">
            <div className="text-muted text-uppercase mb-2" style={{ fontSize: 11, letterSpacing: '.05em' }}>
              The detail dialog
            </div>
            <div className="border rounded p-3">
              {/* gap-3, not a margin on the tile: a flex gap survives the wrap at narrow widths, where a
                  margin-right would leave the text flush against the tile's left edge (UN-3372 item 9). */}
              <div className="d-flex align-items-start gap-3">
                <AppTile name={form.name} imageUrl={form.imageUrl} size={64} />
                <div style={{ minWidth: 0 }}>
                  <div className="fw-semibold" style={{ fontSize: 18 }}>
                    {form.name || 'Untitled app'}
                  </div>
                  <div className="text-muted" style={{ fontSize: 13 }}>
                    {linkedApp ? `${sourceLabel(linkedApp.source)} · ${typeLabel(form.type)}` : typeLabel(form.type) || '—'}
                  </div>
                  <div className="mt-1">
                    <TypePill type={form.type} />
                  </div>
                </div>
              </div>
              <Row className="mt-3" style={{ fontSize: 13 }}>
                <Col xs="6" className="mb-2">
                  <div className="text-muted">Price</div>
                  {price(form.monthlyPrice)}
                </Col>
                <Col xs="6" className="mb-2">
                  <div className="text-muted">Installs</div>
                  {installCount}
                </Col>
                <Col xs="6" className="mb-2">
                  <div className="text-muted">Placement</div>
                  {placementName(form.placement)}
                </Col>
                <Col xs="6" className="mb-2">
                  <div className="text-muted">ERP compatibility</div>
                  {form.erpCompatibility.length ? form.erpCompatibility.join(', ') : 'All'}
                </Col>
                {form.publishedVersion ? (
                  <Col xs="6" className="mb-2">
                    <div className="text-muted">Version</div>
                    {form.publishedVersion}
                  </Col>
                ) : null}
              </Row>
              {form.tags.length ? (
                <div className="mb-2">
                  {form.tags.map(tag => (
                    <Badge key={tag} color="light" className="text-dark border me-1">
                      {tag}
                    </Badge>
                  ))}
                </div>
              ) : null}
              {form.assets && form.assets.length ? (
                // The gallery as the store's detail dialog shows it: a horizontal strip, video playable.
                <div className="d-flex gap-2 mb-3 pb-1" style={{ overflowX: 'auto' }}>
                  {form.assets.map(asset => (
                    <div key={asset.url} className="border rounded overflow-hidden flex-shrink-0 bg-light">
                      {isVideo(asset) ? (
                        <video
                          src={posterSrc(asset.url)}
                          controls
                          preload="metadata"
                          style={{ width: 240, height: 150, objectFit: 'cover' }}
                        />
                      ) : (
                        <img src={asset.url} alt="" style={{ width: 240, height: 150, objectFit: 'cover' }} />
                      )}
                    </div>
                  ))}
                </div>
              ) : null}
              {form.fullDescription ? (
                // The same trust model as the form's editor: this is the admin's own markup, sanitised
                // server-side on save (jsoup safelist), rendered as the store will render it.
                // eslint-disable-next-line react/no-danger
                <div style={{ fontSize: 14 }} dangerouslySetInnerHTML={{ __html: form.fullDescription }} />
              ) : (
                <div className="text-muted" style={{ fontSize: 13 }}>
                  No full description yet.
                </div>
              )}
            </div>
          </Col>
        </Row>
      </ModalBody>
    </Modal>
  );
};

export default AdvertStorePreview;
